// Elapsed time since a condition became true (ENG-3796).
//
// Exists so the connecting screen's dwell and staggered reveal are driven by one
// clock the component can read, while the decisions themselves stay pure
// functions in gate-decisions.ts that need no timers to test.

import { useEffect, useRef, useState } from 'react';

/**
 * Milliseconds since `active` last became true; 0 while inactive.
 *
 * Ticks on an interval rather than a single timeout because callers reveal
 * several things at different offsets from the same start. The interval is
 * cleared as soon as `until` is reached, so a screen that has finished its
 * animation is not left re-rendering for the rest of the session.
 */
export const useElapsedSince = (active: boolean, until: number, tickMs = 100): number => {
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!active) {
      startedAt.current = undefined;
      setElapsed(0);
      return;
    }

    startedAt.current = Date.now();
    setElapsed(0);

    const id = setInterval(() => {
      const started = startedAt.current;
      if (started === undefined) return;
      const next = Date.now() - started;
      setElapsed(next);
      if (next >= until) clearInterval(id);
    }, tickMs);

    return () => clearInterval(id);
  }, [active, until, tickMs]);

  return elapsed;
};
